'use client';

// @boty/ui - Toast component
// Snackbar-based toast notification system with queue support

import type { ReactNode, SyntheticEvent } from 'react';
import { createContext, useState, useCallback, useEffect } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert, { type AlertColor } from '@mui/material/Alert';

export type ToastSeverity = AlertColor; // 'success' | 'error' | 'warning' | 'info'

export interface ToastMessage {
  id: string;
  message: string;
  severity: ToastSeverity;
  duration: number;
}

export interface ToastContextValue {
  showToast: (message: string, severity?: ToastSeverity, duration?: number) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export interface ToastProviderProps {
  children: ReactNode;
  defaultDuration?: number;
  maxToasts?: number;
}

/**
 * ToastProvider - Provides toast notification context to the app
 * Supports queuing multiple toasts - shows them sequentially
 *
 * @param children - React children
 * @param defaultDuration - Default auto-hide duration in ms (default: 5000)
 * @param maxToasts - Maximum toasts in queue (default: 5, oldest removed when exceeded)
 *
 * @example
 * ```tsx
 * // Wrap your app (inside BotyThemeProvider)
 * <BotyThemeProvider>
 *   <ToastProvider>
 *     <App />
 *   </ToastProvider>
 * </BotyThemeProvider>
 * ```
 */
export function ToastProvider({
  children,
  defaultDuration = 5000,
  maxToasts = 5,
}: ToastProviderProps): ReactNode {
  const [toastQueue, setToastQueue] = useState<ToastMessage[]>([]);
  const [currentToast, setCurrentToast] = useState<ToastMessage | null>(null);
  const [open, setOpen] = useState(false);

  // Process queue - show next toast when current one closes
  useEffect(() => {
    if (toastQueue.length > 0 && currentToast === null) {
      const [next, ...rest] = toastQueue;
      setCurrentToast(next ?? null);
      setToastQueue(rest);
      setOpen(true);
    }
  }, [toastQueue, currentToast]);

  const showToast = useCallback(
    (message: string, severity: ToastSeverity = 'info', duration?: number) => {
      const newToast: ToastMessage = {
        id: crypto.randomUUID(),
        message,
        severity,
        duration: duration ?? defaultDuration,
      };

      setToastQueue((prev) => {
        const updated = [...prev, newToast];
        // Remove oldest if exceeding max
        if (updated.length > maxToasts) {
          return updated.slice(-maxToasts);
        }
        return updated;
      });
    },
    [defaultDuration, maxToasts]
  );

  const success = useCallback(
    (message: string, duration?: number) => showToast(message, 'success', duration),
    [showToast]
  );

  const error = useCallback(
    (message: string, duration?: number) => showToast(message, 'error', duration),
    [showToast]
  );

  const warning = useCallback(
    (message: string, duration?: number) => showToast(message, 'warning', duration),
    [showToast]
  );

  const info = useCallback(
    (message: string, duration?: number) => showToast(message, 'info', duration),
    [showToast]
  );

  const handleClose = useCallback(
    (_event?: SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
        return;
      }
      setOpen(false);
    },
    []
  );

  const handleExited = useCallback(() => {
    // Clear current toast after exit animation, allowing next in queue to show
    setCurrentToast(null);
  }, []);

  const contextValue: ToastContextValue = {
    showToast,
    success,
    error,
    warning,
    info,
  };

  const renderSnackbar = (): ReactNode => {
    if (currentToast === null) {
      return null;
    }

    return (
      <Snackbar
        open={open}
        autoHideDuration={currentToast.duration}
        onClose={handleClose}
        TransitionProps={{ onExited: handleExited }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity={currentToast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {currentToast.message}
        </Alert>
      </Snackbar>
    );
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {renderSnackbar()}
    </ToastContext.Provider>
  );
}

ToastProvider.displayName = 'ToastProvider';

export { ToastContext };
